type Unsubscribe = () => void;

type StateUpdater<State> = Partial<State> | ((previousState: State) => Partial<State>);

type StoreActionFactory<State, Actions> = (
  setState: (updater: StateUpdater<State>) => void,
  getState: () => Readonly<State>,
) => Actions;

type StateListener<State> = (nextState: Readonly<State>, previousState: Readonly<State>) => void;

type StateKeyListener<State, Key extends keyof State> = (
  value: Readonly<State>[Key],
  previousValue: Readonly<State>[Key],
) => void;

type SubscribeKeyOptions = {
  immediate?: boolean;
};

type Store<State extends Record<string, unknown>, Actions extends Record<string, unknown>> = {
  getState: () => Readonly<State>;
  setState: (updater: StateUpdater<State>) => void;
  subscribe: (listener: StateListener<State>) => Unsubscribe;
  subscribeKey: <Key extends keyof State>(
    key: Key,
    listener: StateKeyListener<State, Key>,
    options?: SubscribeKeyOptions,
  ) => Unsubscribe;
  actions: Actions;
};

export default function createStore<State extends Record<string, unknown>, Actions extends Record<string, unknown>>(
  initialState: State,
  createActions: StoreActionFactory<State, Actions>,
): Store<State, Actions> {
  let state: State = { ...initialState };

  const stateListeners = new Set<StateListener<State>>();
  const keyListeners = new Map<keyof State, Set<StateKeyListener<State, keyof State>>>();

  const getState = (): Readonly<State> => state;

  const setState = (updater: StateUpdater<State>): void => {
    const partial = typeof updater === 'function' ? updater(state) : updater;
    const nextState = { ...state, ...partial };
    const previousState = state;

    const changedKeys = (Object.keys(nextState) as Array<keyof State>).filter(
      (key) => !Object.is(previousState[key], nextState[key]),
    );

    if (changedKeys.length === 0) {
      return;
    }

    state = nextState;

    for (const listener of stateListeners) {
      listener(state, previousState);
    }

    for (const key of changedKeys) {
      const listeners = keyListeners.get(key);
      if (!listeners) {
        continue;
      }

      for (const listener of listeners) {
        listener(state[key], previousState[key]);
      }
    }
  };

  const subscribe = (listener: StateListener<State>): Unsubscribe => {
    stateListeners.add(listener);
    return () => {
      stateListeners.delete(listener);
    };
  };

  const subscribeKey = <Key extends keyof State>(
    key: Key,
    listener: StateKeyListener<State, Key>,
    options?: SubscribeKeyOptions,
  ): Unsubscribe => {
    const typedListeners = keyListeners.get(key) ?? new Set<StateKeyListener<State, keyof State>>();
    typedListeners.add(listener as StateKeyListener<State, keyof State>);
    keyListeners.set(key, typedListeners);

    if (options?.immediate) {
      listener(state[key], state[key]);
    }

    return () => {
      const currentListeners = keyListeners.get(key);
      if (!currentListeners) {
        return;
      }

      currentListeners.delete(listener as StateKeyListener<State, keyof State>);
      if (currentListeners.size === 0) {
        keyListeners.delete(key);
      }
    };
  };

  return {
    getState,
    setState,
    subscribe,
    subscribeKey,
    actions: createActions(setState, getState),
  };
}
